var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import CompanyStore from 'stores/CompanyStore/store';
import { Roles } from 'constants/AdminConstants';
import useCoachKnockFeed from 'hooks/useCoachKnockFeed';
import { selectBannerItem, selectBellItems, isProducerCheckinItem, isScoringNudgeItem, feedItemActionUrl, isCheckinInteractive, isCheckinDismissable } from 'utils/KnockFeedUtils';
import toNumberOrNull from 'utils/NumberUtils';
import ProducerNudgeBanner from './ProducerNudgeBanner';
import ProducerKnockBell from './ProducerKnockBell';
/** Re-render cadence so bell items roll off while the page stays open. */
var ROLLOFF_TICK_MS = 60000;
/**
 * The producer in-app notification surface (launch requirement, locked
 * 2026-07-02): ONE Knock feed subscription feeding BOTH the persistent nudge
 * banner and the 8-hour-rolloff bell — a single component mount per page so we
 * never open two websockets for the same user. Mounts in the shared header
 * (player area / desktop web) and the mobile entry header (which the iOS shell
 * renders in its webview, so this same slice covers the phone).
 *
 * Producers only — coaches keep CoachKnockFeed. Dark-safe like the coach feed:
 * no items (or Knock unconfigured) → nothing renders.
 */
var ProducerKnockSurface = function (_a) {
    var _b, _c;
    var page = _a.page;
    var info = CompanyStore.getCompanyInfo();
    var userId = toNumberOrNull((_b = info === null || info === void 0 ? void 0 : info.current_user) === null || _b === void 0 ? void 0 : _b.id);
    var roleId = toNumberOrNull((_c = info === null || info === void 0 ? void 0 : info.current_user) === null || _c === void 0 ? void 0 : _c.role_id);
    var isSalesperson = roleId === Roles.SALESPERSON;
    var _d = useCoachKnockFeed(isSalesperson ? userId : null), items = _d.items, markInteracted = _d.markInteracted;
    var _e = useState(function () { return new Date(); }), now = _e[0], setNow = _e[1];
    // The rolloff tick only matters when this surface can render something —
    // the mobile header mounts this component for every user, and an
    // unconditional interval re-rendered a null component once a minute for
    // the whole session for coaches/admins.
    useEffect(function () {
        if (!isSalesperson) {
            return undefined;
        }
        var id = window.setInterval(function () { return setNow(new Date()); }, ROLLOFF_TICK_MS);
        return function () { return window.clearInterval(id); };
    }, [isSalesperson]);
    if (!isSalesperson) {
        return null;
    }
    var bannerItem = selectBannerItem(items, now);
    var bellItems = selectBellItems(items, now);
    // The tap means "I heard you" — Knock's interacted state is server-side, so
    // acknowledging here clears the banner on every surface (first-ack-wins).
    var acknowledge = function (item) { return markInteracted(item, { action: 'acknowledged' }); };
    /*
     * The scoring nudge's tap NAVIGATES (SCO-504 Owner contract: the tap lands
     * directly on the scoring screen). Acknowledge FIRST and await it —
     * assign() is a full page load that cancels in-flight requests, and losing
     * the interacted write resurrects the card on every surface at next mount.
     * `markInteracted` never rejects (it catches internally), so the
     * navigation always happens. Full-page over SPA navigation on purpose:
     * this component mounts in headers on both router and legacy pages, so a
     * useNavigate() here could not assume a Router context.
     */
    var openAction = function (item, url) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, markInteracted(item, { action: 'opened' })];
                case 1:
                    _a.sent();
                    window.location.assign(url);
                    return [2 /*return*/];
            }
        });
    }); };
    /*
     * The producer's own attendance answer (SCO-470). Same `markInteracted`
     * channel as the coach's Yes/No — the backend attributes the answer by
     * WORKFLOW KEY, not by who is logged in, so a tap here lands as
     * `source: 'producer'` and resolves the producer-stage row before the
     * escalation sweep can promote it to the coach digest.
     */
    /*
     * No expiry revalidation anymore (Owner ruling 2, 2026-08-11): the
     * backend's timeout sweep is gone, a human answer lands over a machine
     * `timeout` (SCO-517), and a producer "No" now ROUTES to the coach rather
     * than being discarded — so there is no window in which sending the
     * answer is worse than dropping it.
     */
    var respond = function (item, action) { return markInteracted(item, { action: action }); };
    var bannerIsCheckin = bannerItem !== null && isProducerCheckinItem(bannerItem);
    var bannerActionUrl = bannerItem !== null && isScoringNudgeItem(bannerItem) ?
        feedItemActionUrl(bannerItem.blocks) :
        null;
    return (_jsxs(_Fragment, { children: [_jsx(ProducerKnockBell, { items: bellItems, onAcknowledge: acknowledge, onRespond: respond, onOpenAction: openAction, now: now, page: page }), bannerItem ? (_jsx(ProducerNudgeBanner, { item: bannerItem, onAcknowledge: function () { return acknowledge(bannerItem); }, onRespond: bannerIsCheckin ? function (action) { return respond(bannerItem, action); } : null, onOpenAction: bannerActionUrl === null ?
                    null :
                    function () { return openAction(bannerItem, bannerActionUrl); }, interactive: bannerIsCheckin && isCheckinInteractive(bannerItem, now), dismissable: bannerIsCheckin && isCheckinDismissable(bannerItem, now) })) : null] }));
};
export default ProducerKnockSurface;
